import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { GroupVideo } from 'src/models/video/_types';
import {
  fetchGroupVideo,
  newGroupVideo,
  updateGroupVideo,
} from 'src/models/video/video';
import NewVideoLayout from 'src/shared/pageComponents/Videos/NewVideoLayout';
import { Identifier } from 'src/types/utilityTypes';
import toast from 'src/utils/toast';
import * as Yup from 'yup';

export const NewGroupVideosPage = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const { videoId } = useParams<{ videoId: string }>();
  const { t } = useTranslation('translation', { keyPrefix: 'group.video' });

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: groupVideo } = useQuery({
    queryKey: ['groupVideo', videoId],
    queryFn: () => fetchGroupVideo({ groupVideoId: videoId as Identifier }),
    enabled: !!videoId && videoId !== 'new',
  });

  const createGroupVideoMutation = useMutation({
    mutationFn: (data: Partial<GroupVideo>) => newGroupVideo({ data }),
    onSuccess: () => {
      toast.success(t('Group video created successfully'));
      queryClient.invalidateQueries(['groupVideos']);
      navigate(`/groups/${groupId}/videos`, { replace: true });
    },
    onError: () => {
      toast.error(t('Failed to create group video'));
    },
  });

  const updateGroupVideoMutation = useMutation({
    mutationFn: (data: Partial<GroupVideo>) =>
      updateGroupVideo({ data, groupVideoId: videoId as Identifier }),
    onSuccess: () => {
      toast.success(t('Group video updated successfully'));
      queryClient.invalidateQueries(['groupVideos']);
      navigate(`/groups/${groupId}/videos`, { replace: true });
    },
    onError: () => {
      toast.error(t('Failed to update group video'));
    },
  });

  const formikProps = {
    initialValues: {
      groupId: groupId,
      name: groupVideo?.name || '',
      description: groupVideo?.description || '',
      fullDescription: groupVideo?.fullDescription || '',
      tags: groupVideo?.tags || [],
      thumbnailUrl: groupVideo?.thumbnailUrl || '',
      url: groupVideo?.url || '',
      sendNotification: groupVideo?.sendNotification || true,
      delayInterval: groupVideo?.delayInterval || '',
      expiresAt: groupVideo?.expiresAt || '',
    },
    validationSchema: Yup.object().shape({
      name: Yup.string().required(t("Name can't be blank")),
    }),
    enableReinitialize: true,
    onSubmit: (values) => {
      if (videoId === 'new') {
        createGroupVideoMutation.mutateAsync(values);
      } else {
        updateGroupVideoMutation.mutateAsync(values);
      }
    },
  };

  return (
    <Formik {...formikProps}>
      {() => (
        <NewVideoLayout
          videoId={videoId}
          groupId={groupId}
          loading={
            createGroupVideoMutation.isLoading ||
            updateGroupVideoMutation.isLoading
          }
        />
      )}
    </Formik>
  );
};
